import { XIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'

type ChipProps = {
  /** Present makes the chip removable; the label names what is being removed. */
  onRemove?: () => void
  removeLabel?: string
  children: ReactNode
  className?: string
}

/**
 * A removable pill — an applied filter value, a selected recipient. The
 * toolbar's on/off filter control is `FilterChip`, which is a different thing:
 * it toggles rather than disappears.
 */
export function Chip({ onRemove, removeLabel, children, className }: ChipProps) {
  const { t } = useTranslation('common')
  return (
    <div
      data-slot="chip"
      className={cn(
        'inline-flex h-6.5 items-center gap-1.5 rounded-full border border-border-strong bg-secondary text-caption text-fg-body',
        onRemove === undefined ? 'px-[11px]' : 'py-0 pr-1.25 pl-[11px]',
        className,
      )}
    >
      {children}
      {onRemove !== undefined && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? t('ui.remove')}
          // The × moves; the glyph inside it does not. Press is a property of
          // the control at every size — the rule that keeps the 12px family
          // still is about animating icons, not about withholding feedback.
          className="flex size-4.5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-surface-3 hover:text-foreground press"
        >
          <XIcon className="size-2.5" aria-hidden />
        </button>
      )}
    </div>
  )
}
